import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useLayoutEffect,
} from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Appstore,
  toggleSideMenuIsOpen,
  toggleExtraMenuIsOpen,
  toggleAdvancedSearchIsOpen,
} from "../../../redux";
import AuthContext from "../../../store/AuthContext";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { navigateToLegacy } from "../../../Enums/LegacyRedirection";
import EventBus, { EventBusValues } from "../../../utils/EventBus";
import { CustomWhatsNewDialog } from "../../Dialog/CustomDialogs/CustomWhatsNewDialog";
import { resolveProfilePictureDisplayUrl } from "../../../utils/profilePictureUrl";
import { PROFILE_PHOTO_UPDATED_EVENT } from "../../../utils/notifyProfilePhotoUpdated";
import ProfilePhoto from "../../Atomos/ProfilePhoto";
import HeaderExtra from "../HeaderExtra/HeaderExtra";
import MemberSearch from "../Navigation/MemberSearch";
import ChevronDown from "../../Atomos/Icons/ChevronDown";
import Bar_chart_4_bars from "../../Atomos/Icons/Bar_chart_4_bars";
import MonitorIcon from "../../Atomos/Icons/MonitorIcon";
import Bell from "../../Atomos/Icons/Bell";
import SendMail from "../../Atomos/Icons/ViewMode/SendMail";
import Search from "../../Atomos/Icons/Search";

interface LandingHeaderProps {
  forceActive?: boolean;
}

const LandingHeader: React.FC<LandingHeaderProps> = ({
  forceActive = false,
}) => {
  const dispatch = useDispatch();
  const { isAuthenticated, loggedPerson, isAuthTransitionActive } =
    useContext(AuthContext);
  const showAuthenticatedNav = isAuthenticated && !isAuthTransitionActive;
  const loginUrl = process.env.REACT_APP_SIGNIN_AZB2C_URL as string;
  const pageState = useSelector((state: Appstore) => state.pageState);
  const { extraMenuIsOpen, advancedSearchIsOpen } = useSelector(
    (state: Appstore) => state.menus,
  );
  const { isStaffMode } = useStaffMode();
  const [profilePhotoRenderKey, setProfilePhotoRenderKey] = useState(0);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const [navContainerWidth, setNavContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (navContainerRef.current) {
      setNavContainerWidth(navContainerRef.current.offsetWidth);
    }
  });

  const [localScrollPosition, setLocalScrollPosition] = useState(0);

  useEffect(() => {
    const findScrollContainer = (): HTMLElement | Window => {
      const contentContainer = document.getElementById("content-container");
      if (contentContainer) {
        return contentContainer;
      }

      const landingRoot = document.querySelector('[class*="LandingPage_root"]');
      if (landingRoot) {
        let parent = landingRoot.parentElement;
        while (parent) {
          const style = window.getComputedStyle(parent);
          if (style.overflowY === "auto" || style.overflowY === "scroll") {
            return parent;
          }
          parent = parent.parentElement;
        }
      }

      return window;
    };

    const scrollContainer = findScrollContainer();

    const handleScroll = () => {
      let y = 0;
      if (scrollContainer === window) {
        y = window.scrollY || window.pageYOffset || 0;
      } else {
        y = (scrollContainer as HTMLElement).scrollTop;
      }
      setLocalScrollPosition(y);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    handleScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollPosition =
    localScrollPosition || (pageState["/"]?.scrollPosition ?? 0);
  const isScrolled = scrollPosition > 160;

  useEffect(() => {
    const imageUpdated = (event: MessageEvent) => {
      const { data } = event;
      if (data.type === "imageUpdated" && loggedPerson?.id) {
        setProfilePhotoRenderKey((k) => k + 1);
      }
    };
    const onProfilePhotoUpdated = (event: Event) => {
      const { personId } = (
        event as CustomEvent<{ personId: string; cacheBust: number }>
      ).detail;
      if (personId && personId === loggedPerson?.id) {
        setProfilePhotoRenderKey((k) => k + 1);
      }
    };

    window.addEventListener("message", imageUpdated);
    document.addEventListener(PROFILE_PHOTO_UPDATED_EVENT, onProfilePhotoUpdated);
    return () => {
      window.removeEventListener("message", imageUpdated);
      document.removeEventListener(
        PROFILE_PHOTO_UPDATED_EVENT,
        onProfilePhotoUpdated,
      );
    };
  }, [loggedPerson?.id]);

  const profilePhotoSrc = resolveProfilePictureDisplayUrl(
    loggedPerson?.id,
    loggedPerson?.photo,
    { photoUpdatedAt: loggedPerson?._ts },
  );

  const toggleHeaderExtra = () => {
    if (advancedSearchIsOpen) {
      dispatch(toggleAdvancedSearchIsOpen());
    }
    dispatch(toggleExtraMenuIsOpen());
  };

  const handleMenuClick = () => {
    dispatch(toggleSideMenuIsOpen());
  };

  const isActiveState =
    forceActive || isScrolled || advancedSearchIsOpen || extraMenuIsOpen;

  const headerBg = isActiveState ? "bg-white shadow-md" : "bg-transparent";
  const textColor = isActiveState ? "text-gray-font" : "text-white";

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      <div className="flex items-center justify-between px-4 py-6">
        <div className="flex items-center h-10">
          <Link
            to="/"
            className={`flex items-center transition-opacity duration-300 ${isActiveState ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <div className="w-[5.875rem] h-[2.5rem] md:w-[10.25rem] md:h-[4.5rem]">
              <img
                src="https://members-staging.worldservicesgroup.com/assets/images/logo.svg"
                alt="WSG Logo"
                className="h-full w-full object-contain"
              />
            </div>
          </Link>
        </div>

        <div
          ref={navContainerRef}
          className="flex items-center gap-2 md:gap-8 ml-auto h-10"
        >
          {!showAuthenticatedNav ? (
            <Link
              to={loginUrl}
              className={`flex items-center gap-4 hover:opacity-80 transition-opacity`}
            >
              <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10">
                <svg className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 41 41">
                  <circle
                    cx="20.5"
                    cy="20.5"
                    r="20.5"
                    fill={isActiveState ? "#474F56" : "#fff"}
                  />
                  <g fill={isActiveState ? "#474F56" : "#fff"}>
                    <circle cx="20.5" cy="13.3" r="5" />
                    <path d="M28.9 32.7H12.1v-7.1c0-2.8 2.3-5.1 5.1-5.1h6.4c2.8 0 5.1 2.3 5.1 5.1v7.1z" />
                  </g>
                  <g fill={isActiveState ? "#fff" : "#333"}>
                    <circle cx="20.5" cy="13.3" r="5" />
                    <path d="M28.9 32.7H12.1v-7.1c0-2.8 2.3-5.1 5.1-5.1h6.4c2.8 0 5.1 2.3 5.1 5.1v7.1z" />
                  </g>
                </svg>
              </div>
              <div
                className={`hidden md:flex flex-col text-[11px] md:text-[13px] tracking-wider uppercase leading-tight ${textColor}`}
                style={{
                  fontFamily: "Decimal, sans-serif",
                  fontWeight: "1000",
                }}
              >
                <span>MEMBER</span>
                <span>LOG IN</span>
              </div>
            </Link>
          ) : (
            <div className="flex items-center gap-0 md:gap-6 mr-0">
              {loggedPerson?.firmAdminAccess && (
                <Link
                  to="/dashboard.asp"
                  className={`flex items-center justify-center hover:opacity-80 transition-opacity ${textColor}`}
                >
                  <div className="w-5 h-5 md:w-6 md:h-6 mr-1 flex items-center justify-center">
                    <Bar_chart_4_bars />
                  </div>
                  <span className="text-sm md:text-base font-bold hidden md:block font-roboto">
                    Dashboard
                  </span>
                </Link>
              )}

              {isStaffMode && (
                <button
                  className={`flex items-center justify-center hover:opacity-80 transition-opacity ${textColor}`}
                  onClick={() => navigateToLegacy(isStaffMode)}
                >
                  <div className="w-5 h-5 md:w-6 md:h-6 mr-1 flex items-center justify-center">
                    <MonitorIcon />
                  </div>
                  <span className="text-sm md:text-base font-bold hidden md:block font-roboto">
                    Go to Legacy
                  </span>
                </button>
              )}

              <button
                className={`flex items-center justify-center hover:opacity-80 transition-opacity ${textColor}`}
                onClick={() =>
                  EventBus.dispatch(EventBusValues.OpenDialog, {
                    element: <CustomWhatsNewDialog />,
                    customContent: true,
                  })
                }
              >
                <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                  <Bell />
                </div>
                <span className="text-sm md:text-base font-bold hidden md:block font-roboto ml-1">
                  Notifications
                </span>
              </button>

              <Link
                to="/wsg_mailbox.asp"
                title="Inbox"
                className={`w-5 h-5 md:w-6 md:h-6 flex items-center justify-center hover:opacity-80 transition-opacity ${textColor}`}
              >
                <SendMail />
              </Link>

              <button
                className={`flex items-center gap-0 md:gap-2 hover:opacity-80 transition-all px-0 md:px-0 py-1 rounded ${extraMenuIsOpen ? "text-Default" : textColor}`}
                onClick={toggleHeaderExtra}
              >
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {loggedPerson && (
                    <ProfilePhoto
                      src={profilePhotoSrc}
                      imgKey={`${loggedPerson.id}-${profilePhotoRenderKey}`}
                      alt="profile"
                      imgClassName="h-full w-full object-cover object-center"
                    />
                  )}
                </div>
                <span
                  className="font-bold hidden md:block text-sm md:text-base font-roboto"
                  style={{
                    fontFamily: "Decimal, sans-serif",
                    fontWeight: "1000",
                  }}
                >
                  {loggedPerson?.name} {loggedPerson?.middleInitial}{" "}
                  {loggedPerson?.lastName}
                </span>
                <div
                  className={`w-4 h-4 md:w-5 md:h-5 ${extraMenuIsOpen ? "text-Default" : textColor}`}
                >
                  <ChevronDown
                    className={`${extraMenuIsOpen ? "text-Default" : textColor}`}
                    rotation={
                      extraMenuIsOpen ? "rotate(180deg)" : "rotate(0deg)"
                    }
                  />
                </div>
              </button>
            </div>
          )}

          <button
            onClick={() => {
              if (extraMenuIsOpen) {
                dispatch(toggleExtraMenuIsOpen());
              }
              dispatch(toggleAdvancedSearchIsOpen());
            }}
            className={`flex items-center gap-0 md:gap-4 hover:opacity-80 transition-all px-0 md:px-0 py-1 rounded`}
          >
            <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10">
              <svg
                width="32"
                height="32"
                viewBox="0 0 22.2 22.2"
                className="w-8 h-8 md:w-10 md:h-10"
              >
                <defs>
                  <path id="svgA" d="M0 0h22.2v22.2H0z"></path>
                </defs>
                <clipPath id="svgB">
                  <use xlinkHref="#svgA"></use>
                </clipPath>
                <g
                  fill="none"
                  stroke={
                    advancedSearchIsOpen
                      ? "#0070cd"
                      : isActiveState
                        ? "#474F56"
                        : "#fff"
                  }
                  strokeWidth="2.842"
                >
                  <circle
                    cx="9.4"
                    cy="9.4"
                    r="8"
                    clipPath="url(#svgB)"
                  ></circle>
                  <path
                    d="M15 15l5.7 5.7"
                    clipPath="url(#svgB)"
                    strokeLinecap="round"
                    strokeMiterlimit="10"
                  ></path>
                </g>
                <g
                  fill={
                    advancedSearchIsOpen
                      ? "#0070cd"
                      : isActiveState
                        ? "#474F56"
                        : "#fff"
                  }
                >
                  <path
                    d="M12.6 7.9c0 1.8-1.4 3.2-3.2 3.2S6.2 9.7 6.2 7.9s1.4-3.2 3.2-3.2c1.7 0 3.2 1.4 3.2 3.2"
                    clipPath="url(#svgB)"
                  ></path>
                  <path
                    d="M9.4 10.3c-2.6 0-4.8 2.1-4.8 4.8v.6c1.3 1 2.9 1.7 4.7 1.7s3.4-.6 4.7-1.7v-.6c.2-2.7-2-4.8-4.6-4.8"
                    clipPath="url(#svgB)"
                  ></path>
                </g>
              </svg>
            </div>
            <div
              className={`hidden md:flex flex-row items-center gap-2 text-[11px] md:text-[13px] tracking-wider uppercase leading-tight ${advancedSearchIsOpen ? "text-Default" : textColor}`}
              style={{ fontFamily: "Decimal, sans-serif", fontWeight: "1000" }}
            >
              <div className="flex flex-col leading-tight">
                <span>MEMBER</span>
                <span>SEARCH</span>
              </div>
              <div className="w-4 h-4">
                <ChevronDown
                  className={`${advancedSearchIsOpen ? "text-Default" : textColor}`}
                  rotation={
                    advancedSearchIsOpen ? "rotate(180deg)" : "rotate(0deg)"
                  }
                />
              </div>
            </div>
            <div
              className={`w-4 h-4 md:hidden ${advancedSearchIsOpen ? "text-Default" : textColor}`}
            >
              <ChevronDown
                rotation={
                  advancedSearchIsOpen ? "rotate(180deg)" : "rotate(0deg)"
                }
              />
            </div>
          </button>

          <button
            onClick={handleMenuClick}
            className={`relative flex items-center justify-center border-none p-0 m-0 -ml-2 md:-ml-1 bg-transparent cursor-pointer w-16 md:w-[4.3rem] font-bold text-xs md:text-[1.3rem] leading-none tracking-tight font-roboto text-center uppercase transition-all duration-[350ms] ease-in-out hover:opacity-80 ${textColor}`}
          >
            <div className="relative w-[38px] md:w-[50px] h-8 md:h-10 flex flex-col items-center justify-center">
              <span
                className={`block absolute left-0 w-full h-[0.4rem] md:h-[0.45rem] rounded-none opacity-100 top-0 transition-all duration-[350ms] ease-in-out ${
                  isActiveState ? "bg-primary" : "bg-white"
                }`}
                style={{ transform: "rotate(0deg)" }}
              ></span>
              <span
                className={`block text-[11px] md:text-[13px] font-semibold tracking-wider uppercase transition-all duration-[350ms] ease-in-out ${
                  isActiveState ? "text-primary" : "text-white"
                }`}
                style={{
                  fontFamily: "Decimal, sans-serif",
                  fontWeight: "1000",
                }}
              >
                MENU
              </span>
              <span
                className={`block absolute left-0 w-full h-[0.4rem] md:h-[0.45rem] rounded-none opacity-100 bottom-0 transition-all duration-[350ms] ease-in-out ${
                  isActiveState ? "bg-primary" : "bg-white"
                }`}
                style={{ transform: "rotate(0deg)" }}
              ></span>
            </div>
          </button>
        </div>
      </div>

      {showAuthenticatedNav && (
        <div
          className={`absolute z-40 bg-gray-50 align-middle h-auto justify-center
          flex shadow-md shadow-black/50 top-[5.5rem] rounded-md right-0
          transition-all duration-300 ease-in-out transform
          ${
            extraMenuIsOpen
              ? "translate-y-0 opacity-100 pointer-events-auto"
              : "-translate-y-5 opacity-0 pointer-events-none"
          }
        `}
          style={{
            width: navContainerWidth > 0 ? navContainerWidth + 20 : "auto",
          }}
        >
          <HeaderExtra />
        </div>
      )}

      <div className="absolute left-1/2 -translate-x-1/2 top-0 w-[95vw] max-w-[1440px] h-0 px-4 sm:px-8 md:px-16 lg:px-24 [&>div]:!left-0 [&>div]:!right-0 [&>div]:!top-[5.5rem] lg:[&>div]:!left-auto">
        <MemberSearch />
      </div>
    </div>
  );
};

export default LandingHeader;
